// Version : 0.0.1
// Author : Vignesh
// LastEdit : Vignesh
// Last ADD : Show User Control Access tab only to the ADMIN - Level100 Access
// MODFIED DATE : 31-07-2025

/* eslint-disable react/react-in-jsx-scope */
import { Fragment, ReactNode, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Brand from '../../../layout/Brand/Brand';
import Navigation, { NavigationLine } from '../../../layout/Navigation/Navigation';
import User from '../../../layout/User/User';
import ThemeContext from '../../../contexts/themeContext';
import Aside, { AsideBody, AsideFoot, AsideHead } from '../../../layout/Aside/Aside';
import {
	financePageMenu,
	masterPageMenu,
	operationsPageMenu,
	userControlAccessPageMenu,
	reportsPageMenu,
	companyPageMenu,
	// autoLoanPageMenu,
} from '../../../menu';
import { getLoginObjDetails } from '../../../redux/login/login.selector';
import {
	applyMenuAccessControl,
	buildMenuAccessMap,
	cloneMenuDefinition,
} from '../../../utils/menuAccess';

const hasMenuItems = (menu: Record<string, unknown> | undefined): boolean =>
	Boolean(menu && Object.keys(menu).length > 0);

const DefaultAside = () => {
	const { asideStatus, setAsideStatus } = useContext(ThemeContext);
	const loginData = useSelector(getLoginObjDetails);
	console.log('sds', loginData);

	const accessMap = useMemo(
		() => buildMenuAccessMap(loginData.userAccessData),
		[loginData.userAccessData],
	);

	const companyMenu = useMemo(
		() => applyMenuAccessControl(companyPageMenu, accessMap),
		[accessMap],
	);

	const userAccessMenuPrePared = useMemo(() => {
		const menuClone = cloneMenuDefinition(userControlAccessPageMenu);
		const subMenu = menuClone.userControlAccessPages?.subMenu as
			| Record<string, any>
			| undefined;

		if (subMenu?.workFlowManagement) {
			subMenu.workFlowManagement.hide = loginData.isWorkFlowNeeded != '1';
		}
		return menuClone;
	}, [loginData.isWorkFlowNeeded]);

	const userControlMenu = useMemo(
		() => applyMenuAccessControl(userAccessMenuPrePared, accessMap),
		[userAccessMenuPrePared, accessMap],
	);

	const mastersMenuPrepared = useMemo(() => {
		const menuClone = cloneMenuDefinition(masterPageMenu);
		const subMenu = menuClone.masterPages?.subMenu as Record<string, any> | undefined;

		if (subMenu?.dealer) {
			subMenu.dealer.hide = loginData.isAutoLoan != '1';
		}
		if (subMenu?.business) {
			subMenu.business.hide = loginData.showBusinessLoan != '1';
		}
		if (subMenu?.savingsPage) {
			subMenu.savingsPage.hide = loginData.isThrift != '1';
		}
		return menuClone;
	}, [loginData.isThrift, loginData.showBusinessLoan, loginData.isAutoLoan]);

	const masterMenu = useMemo(
		() => applyMenuAccessControl(mastersMenuPrepared, accessMap),
		[accessMap, mastersMenuPrepared],
	);

	const operationsMenuPrepared = useMemo(() => {
		const menuClone = cloneMenuDefinition(operationsPageMenu);
		const subMenu = menuClone.operationPages?.subMenu as Record<string, any> | undefined;

		if (subMenu?.GoldLoanProposal) {
			subMenu.GoldLoanProposal.hide = loginData.isGoldLoanAvailed != '1';
		}
		if (subMenu?.goldCollectionPage) {
			subMenu.goldCollectionPage.hide = loginData.isGoldLoanAvailed != '1';
		}
		if (subMenu?.BusinessLoanProposal) {
			subMenu.BusinessLoanProposal.hide = loginData.showBusinessLoan != '1';
		}
		if (subMenu?.businessLoanDisbursementPage) {
			subMenu.businessLoanDisbursementPage.hide = loginData.showBusinessLoan != '1';
		}
		if (subMenu?.businessCollectionPage) {
			subMenu.businessCollectionPage.hide = loginData.showBusinessLoan != '1';
		}
		if (subMenu?.goldLoanDisbursementPage) {
			subMenu.goldLoanDisbursementPage.hide = loginData.isGoldLoanAvailed != '1';
		}
		if (subMenu?.autoLoanDisbursemetPage) {
			subMenu.autoLoanDisbursemetPage.hide = loginData.isAutoLoan != '1';
		}
		if (subMenu?.autoLoanProposalPage) {
			subMenu.autoLoanProposalPage.hide = loginData.isAutoLoan != '1';
		}
		if (subMenu?.autoLoanCollectionPage) {
			subMenu.autoLoanCollectionPage.hide = loginData.isAutoLoan != '1';
		}
		if (subMenu?.RDProposal) {
			subMenu.RDProposal.hide = loginData.isThrift != '1';
		}
		if (subMenu?.IndividualLoanProposal) {
			subMenu.IndividualLoanProposal.hide = loginData.isIndividualLoanNeeded != '1';
		}
		if (subMenu?.closeLoanAdvancePage) {
			subMenu.closeLoanAdvancePage.hide = loginData.isThrift != '1';
		}
		if (subMenu?.goldCloseLoanPage) {
			subMenu.goldCloseLoanPage.hide = loginData.isGoldLoanAvailed != '1';
		}
		return menuClone;
	}, [
		loginData.isGoldLoanAvailed,
		loginData.isThrift,
		loginData.showBusinessLoan,
		loginData.isAutoLoan,
		loginData.isIndividualLoanNeeded,
	]);

	const operationsMenu = useMemo(
		() => applyMenuAccessControl(operationsMenuPrepared, accessMap),
		[operationsMenuPrepared, accessMap],
	);

	const reportsMenuPrepared = useMemo(() => {
		const menuClone = cloneMenuDefinition(reportsPageMenu);
		const subMenu = menuClone.reportsPages?.subMenu as Record<string, any> | undefined;

		if (subMenu?.goldLoanCollectionReport) {
			subMenu.goldLoanCollectionReport.hide = loginData.isGoldLoanAvailed != '1';
		}
		if (subMenu?.goldLoanStockReport) {
			subMenu.goldLoanStockReport.hide = loginData.isGoldLoanAvailed != '1';
		}
		if (subMenu?.goldLoanInterestReminderReport) {
			subMenu.goldLoanInterestReminderReport.hide = loginData.isGoldLoanAvailed != '1';
		}
		if (subMenu?.goldPriceReport) {
			subMenu.goldPriceReport.hide = loginData.isGoldLoanAvailed != '1';
		}
		if (subMenu?.attendanceReport) {
			subMenu.attendanceReport.hide = loginData.isOfficerAttendanceNeeded != '1';
		}
		if (subMenu?.goldArrearReport) {
			subMenu.goldArrearReport.hide = loginData.isGoldLoanAvailed != '1';
		}
		if (subMenu?.qrReport) {
			subMenu.qrReport.hide = loginData.showQRPayment != '1';
		}
		if (subMenu?.AutoCollectionPageReport) {
			subMenu.AutoCollectionPageReport.hide = loginData.isAutoLoan != '1';
		}
		if (subMenu?.mandateReport) {
			subMenu.mandateReport.hide = loginData.enachVia == '' || loginData.enachVia == null;
		}
		if (subMenu?.enachReport) {
			subMenu.enachReport.hide = loginData.enachVia == '' || loginData.enachVia == null;
		}
		if (subMenu?.goldArrearReport) {
			subMenu.goldArrearReport.hide = loginData.isGoldLoanAvailed != '1';
		}
		if (subMenu?.goldLoanInterestReminderReport) {
			subMenu.goldLoanInterestReminderReport.hide = loginData.isGoldLoanAvailed != '1';
		}
		if (subMenu?.savingsFollowupReport) {
			subMenu.savingsFollowupReport.hide = loginData.isIndividualSavings != '1';
		}
		if (subMenu?.memberAccountReport) {
			subMenu.memberAccountReport.hide = loginData.isIndividualSavings != '1';
		}
		return menuClone;
	}, [
		loginData.isGoldLoanAvailed,
		loginData.isAutoLoan,
		loginData.showQRPayment,
		loginData.enachVia,
		loginData.isOfficerAttendanceNeeded,
		loginData.isIndividualSavings,
	]);

	const reportsMenu = useMemo(
		() => applyMenuAccessControl(reportsMenuPrepared, accessMap),
		[accessMap, reportsMenuPrepared],
	);

	const financeMenu = useMemo(
		() => applyMenuAccessControl(financePageMenu, accessMap),
		[accessMap],
	);

	const renderMenuBlocks = (blocks: ReactNode[]) =>
		blocks.map((block, index) => (
			<Fragment key={`aside-menu-block-${index}`}>
				{block}
				{index < blocks.length - 1 && <NavigationLine />}
			</Fragment>
		));

	const superAdminMenuBlocks: ReactNode[] = [];
	if (hasMenuItems(companyMenu)) {
		superAdminMenuBlocks.push(<Navigation menu={companyMenu} id='aside-company-page' />);
	}

	const regularMenuBlocks: ReactNode[] = [];

	if (
		loginData.userLevel === 'level100' &&
		loginData.userLevel !== 'level4' &&
		loginData.userLevel !== 'level5' &&
		hasMenuItems(userControlMenu)
	) {
		regularMenuBlocks.push(
			<Navigation menu={userControlMenu} id='aside-userControlAccess-pages' />,
		);
	}
	if (hasMenuItems(masterMenu)) {
		regularMenuBlocks.push(<Navigation menu={masterMenu} id='aside-master-pages' />);
	}
	if (hasMenuItems(operationsMenu)) {
		regularMenuBlocks.push(<Navigation menu={operationsMenu} id='aside-operation-pages' />);
	}
	if (loginData.companyIsActive == 1 && hasMenuItems(reportsMenu)) {
		regularMenuBlocks.push(<Navigation menu={reportsMenu} id='aside-report-pages' />);
	}
	if (hasMenuItems(financeMenu)) {
		regularMenuBlocks.push(<Navigation menu={financeMenu} id='aside-finance-pages' />);
	}

	return (
		<Aside>
			<AsideHead>
				<Brand asideStatus={asideStatus} setAsideStatus={setAsideStatus} />
			</AsideHead>

			<AsideBody>
				<>
					{loginData.userType === 'SuperAdmin'
						? renderMenuBlocks(superAdminMenuBlocks)
						: renderMenuBlocks(regularMenuBlocks)}
				</>
			</AsideBody>
			<AsideFoot>
				<User />
			</AsideFoot>
		</Aside>
	);
};
export default DefaultAside;
